import React, {useCallback, useContext, useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Dropdown, Form, InputGroup, Modal, OverlayTrigger, Popover, Table, Tooltip } from "react-bootstrap";
import { useAppSelector } from "../../redux/hooks";
import { lddStateObject, setIndicators, setListDataState, setShowPackageSaveWarning } from '../../redux/reducer'
import { useDispatch, useSelector } from "react-redux";
import { NavObjectWinState, lddGlobalSessionInfo } from "../navigationTypes";
import { NavigationContext } from "../../Context/NavigationContext";
import { ActivityCenterContext } from "../../Context/ActivityCenter";
import { navigatePopupPage } from "../../compatManager";
import { Tag, Creator, Document, User, Custom, creators, PSM, P, S, M, psm, Category, Package, CompoundDocument } from "./types/Types";
import { DocMastersFilter } from "./Filter";
import { PackagesEditor } from "./PackagesEditor";
import { navDocEditor, hasSomeTags } from "./DocumentsFuncs";
import { getPackageList, updatePackageList, deactivatePackageList } from "./PackagesFuncs";
import Loading from "../Loading";

const Packages = () => {
    const SESSION_INFO: lddGlobalSessionInfo = useAppSelector((g) => g.SessionInfo);
    const nav = useContext(NavigationContext);
    const activityCenter = useContext(ActivityCenterContext);
    const dispatch = useDispatch();

    const [packages, setPackages] = useState<Package[]>([]);
    const [pkgSelect, setPkgSelect] = useState<Package>(null);
    const [searchPkgText, setSearchPkgText] = useState<string>('');
    const [debouncedSearchPkgText, setDebouncedSearchPkgText] = useState<string>('');
    const [filtersSelect, setFiltersSelect] = useState<Array<Tag | PSM | Creator | Category | Package>>([]);
    const [pkgsExpand, setPkgsExpand] = useState<Array<Package>>([]);
    const [pkgsShowCondition, setPkgsShowCondition] = useState<Array<Package>>([]);

    const [showPkgEditor, setShowPkgEditor] = useState<boolean>(false);
    const [showDeleteAlert, setShowDeleteAlert] = useState<boolean>(false);
    const [showDeactivateAlert, setShowDeactivateAlert] = useState<boolean>(false);
    const showPackageSaveWarning = useSelector((state: lddStateObject) => state.ShowPackageSaveWarning);
    const [pkgEditorAction, setPkgEditorAction] = useState<"CreateNew" | "AddDoc" | "Rename">("CreateNew");
    const [isPackagesDirty, setIsPackagesDirty] = useState<boolean>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        async function initPkgList() {
            try {
                const pkgList = await getPackageList(SESSION_INFO.appProvinceShort, SESSION_INFO.firm.id.toString(), SESSION_INFO.sid);
                setPackages(pkgList);
            } catch (error) {
                console.error("Error loading packages: ", error);
            } finally {
                setIsLoading(false);
            }
        }
        dispatch(setShowPackageSaveWarning(false));
        setIsLoading(true);
        initPkgList();
    }, []);

    useEffect(() => {
        const autoExpandPackages = searchPkgText ? pkgsExpand : [];
        setPkgsExpand(autoExpandPackages);
    }, [searchPkgText]);

    // update search text 500ms after users stop typing
    useEffect(() => {
        const handler = setTimeout(() => {
            setSearchPkgText(debouncedSearchPkgText);
        }, 500);
        return () => {
          clearTimeout(handler);
        };
    }, [debouncedSearchPkgText]);

    useEffect(() => {
        dispatch(setListDataState("PackageList", packages));
        // isPackagesDirty null -> page clean as default while no event triggered
        if (isPackagesDirty !== null) {
            dispatch(setIndicators("IsPageDirty", isPackagesDirty));
            setIsPackagesDirty(null);
        }
    }, [isPackagesDirty]);

    const pkgsFiltered = packages.map((pkgs) => {
        let children = pkgs.children?.filter((pkg) => {
            let isSearchMatch = true;
            if (searchPkgText > '') {
                const isPkgSearchMatch = pkg.name.toLowerCase().includes(searchPkgText.toLowerCase());
                isSearchMatch = isPkgSearchMatch;
            }
            let isFilterMatch = true;
            filtersSelect.forEach((tag) => {
                if (!pkgs.tags.includes(tag) && !pkg.tags.includes(tag)) {
                    isFilterMatch = false;
                }
            });

            return isSearchMatch && isFilterMatch;
        })

        return {
            ...pkgs,
            children
        };
    });

    const handleFilterClick = useCallback((tag: Tag | Category | Package) => {
        if (filtersSelect.includes(tag)) {
            setFiltersSelect(filtersSelect.filter((t) => t !== tag));
        } else {
            setFiltersSelect([...filtersSelect, tag]);
        }
    }, [filtersSelect, setFiltersSelect]);

    const filterPopover = (
        <Popover className="filter-container">
            <DocMastersFilter
                filtersSelect={filtersSelect}
                setFiltersSelect={setFiltersSelect}
                screenType="Packages"
            />
        </Popover>
    );

    const handlePkgEditor = (action: "CreateNew" | "AddDoc" | "Rename", existingPkg?: Package) => {
        setPkgEditorAction(action);
        setPkgSelect(existingPkg?? null);
        setShowPkgEditor(true);
    }

    const handlePkgClick = (pkg: Package) => {
        if (pkgsExpand.some((p) => p.name === pkg.name)) {
            setPkgsExpand(pkgsExpand.filter((p) => p.name !== pkg.name));
        } else {
            setPkgsExpand([...pkgsExpand, pkg]);
        }
    }

    const handlePkgCondtionSwitch = (pkg: Package) => {
        if (pkgsShowCondition.some((p) => p.name === pkg.name)) {
            setPkgsShowCondition(pkgsShowCondition.filter((p) => p.name !== pkg.name));
            setPkgsExpand(pkgsExpand.filter((p) => p.name !== pkg.name));
        } else {
            setPkgsShowCondition([...pkgsShowCondition, pkg]);
            setPkgsExpand([...pkgsExpand, pkg]);
        }
    }

    const handlePkgCopy = (pkg: Package) => {
        const prePkgName = pkg.name;
        setPackages(packages => {
            let newPkgName = `Copy of ${prePkgName}`;
            const allPkgNames = packages.flatMap((pkgs) => pkgs.children ?? []).map((p) => p.name);
            while (allPkgNames.includes(newPkgName)) {
                newPkgName = `Copy of ${newPkgName}`;
            }
            const newPkg: Package = {
                ...pkg,
                name: newPkgName,
                tags: pkg.tags,
                doc_list: pkg.doc_list?.map(d => ({
                  ...d,
                  tags: d.tags,
                })) ?? [],
              };
            return packages.map((pkgs) => {
                if (pkgs.children?.some((p) => p.name === prePkgName && hasSomeTags([p, pkg], psm))) {
                    return {
                        ...pkgs,
                        children: pkgs.children.concat(newPkg)
                    };
                } else {
                    return pkgs;
                }
            });
        });
        setIsPackagesDirty(true);
    }

    const handlePkgDelete = (pkg: Package) => {
        const newPackages = packages.map((pkgs) => {
            if (pkgs.children?.some((p) => p.name === pkg.name && hasSomeTags([p, pkg], psm))) {
                return {
                    ...pkgs,
                    children: pkgs.children.filter((p) => p.name !== pkg.name)
                };
            } else {
                return pkgs;
            }
        });
        setPackages(newPackages);
        savePackages(newPackages);
    }

    const moveItem = (arr, curIdx, targetIdx) => {
        const item = arr[curIdx];
        const newArr = arr.filter((_, index) => index !== curIdx);
        newArr.splice(targetIdx, 0, item);
        return newArr;
    };

    const handlePkgMove = (pkg: Package, pkgIdx: number, direction: "up" | "down") => {
        setPackages(packages.map((pkgs) => {
            if (hasSomeTags([pkgs, pkg], psm) && pkgs.children[pkgIdx]?.name === pkg.name) {   
                let targetIdx = direction === "up" ? pkgIdx - 1 : pkgIdx + 1;
                if (targetIdx < 0 || targetIdx >= pkgs.children.length)
                    return pkgs;
                return {
                    ...pkgs,
                    children: moveItem(pkgs.children, pkgIdx, targetIdx)
                };
            } else {
                return pkgs;
            }
        }));
        setIsPackagesDirty(true);
    }

    const handleDocMove = (doc: Document, docIdx: number, pkg: Package, direction: "up" | "down") => {
        setPackages(packages.map((pkgs) => {
            if (hasSomeTags([pkgs, pkg], psm)) {
                return {
                    ...pkgs,
                    children: pkgs.children.map((p) => {
                        if (p.name === pkg.name && p.doc_list[docIdx]?.name === doc.name) {
                            let targetIdx = direction === "up" ? docIdx - 1 : docIdx + 1;
                            if (targetIdx < 0 || targetIdx >= p.doc_list.length)
                                return p;
                            return {
                                ...p,
                                doc_list: moveItem(p.doc_list, docIdx, targetIdx)
                            };
                        } else {
                            return p;
                        }
                    })
                };
            } else {
                return pkgs;
            }
        }));
        setIsPackagesDirty(true);
    }

    const handleDocDelete = (doc: Document, docIdx: number, pkg: Package) => {
        setPackages(packages.map((pkgs) => {
            if (hasSomeTags([pkgs, pkg], psm)) {
                return {
                    ...pkgs,
                    children: pkgs.children.map((p) => {
                        if (p.name === pkg.name && p.doc_list[docIdx]?.name === doc.name) {
                            return {
                                ...p,
                                doc_list: p.doc_list.filter((_, i) => i !== docIdx),
                            };
                        } else {
                            return p;
                        }
                    })
                };
            } else {
                return pkgs;
            }
        }));
        setIsPackagesDirty(true);
    }

    const handleDocSetCondition = (docSelect: Document, docSelectIdx: number, pkgSelect: Package) => {
        const windowState: NavObjectWinState = {
            dialogArguments: new Array<string>(docSelect.pkg_condition?? ""),
        };

        const ret = navigatePopupPage("documentConditions.asp?SessionID=" + SESSION_INFO.sid, null, true, windowState.dialogArguments);
        ret.then((conditionText) => {
            if (conditionText !== null && conditionText != docSelect.pkg_condition) {
                const newPackages = packages.map((pkgs) => {
                    if (hasSomeTags([pkgs, pkgSelect], psm)) {
                        return {
                            ...pkgs,
                            children: pkgs.children.map((pkg) => {
                                if (pkg.name === pkgSelect.name) {
                                    return {
                                        ...pkg,
                                        doc_list: pkg.doc_list.map((doc, docIdx) => {
                                            if (docIdx === docSelectIdx && doc === pkg.doc_list[docSelectIdx])
                                                return {
                                                    ...doc,
                                                    pkg_condition: conditionText ?? "",
                                                };
                                            return doc;
                                        })
                                    };
                                }
                                return pkg;
                            })
                        };
                    }
                    return pkgs;
                });
                setPackages(newPackages);
                savePackages(newPackages);
            }
        });
    }

    const handleDocPageNumberReset = (docSelectIdx: number, pkgSelect: Package) => {
        setPackages(packages.map((pkgs) => {
            if (hasSomeTags([pkgs, pkgSelect], psm)) {
                return {
                    ...pkgs,
                    children: pkgs.children.map((pkg) => {
                        if (pkg.name === pkgSelect.name) {
                            return {
                                ...pkg,
                                doc_list: pkg.doc_list.map((doc, docIdx) => {
                                    if (docIdx === docSelectIdx && doc === pkg.doc_list[docSelectIdx])
                                        return {
                                            ...doc,
                                            tags: doc.tags.some((tag) => tag.name === "NoPageReset") ? doc.tags.filter((tag) => tag.name !== "NoPageReset") : [...doc.tags, { name: "NoPageReset" } as Tag]
                                        };
                                    return doc;
                                })
                            };
                        }
                        return pkg;
                    })
                };
            }
            return pkgs;
        }));
        setIsPackagesDirty(true);
    }

    const savePackages = async (newPackages: Package[]) => {
        // trigger a save once creating, modifying or delete a package inside modal
        // if save is successful, set page clean, otherwise go through regular page dirty workflow
        try {
            await updatePackageList(SESSION_INFO.appProvinceShort, SESSION_INFO.firm.id.toString(), SESSION_INFO.sid, newPackages)
                .then((res) => {
                    if (res) {
                        activityCenter.Add("Package List Saved Successfully", undefined, "save", null);
                        setIsPackagesDirty(false);
                    }
                });
        } catch (e) {
            console.error("Error savePackages: ", e);
            setIsPackagesDirty(true);
        }
        dispatch(setShowPackageSaveWarning(true));
    }

    const deactivePackages = async () => {
        setIsLoading(true);
        try {
            await deactivatePackageList(SESSION_INFO.appProvinceShort, SESSION_INFO.firm.id.toString(), SESSION_INFO.sid)
                .then((res) => {
                    if (res) {
                        activityCenter.Add("Package List Reset Successfully", undefined, "save", null);
                    }
                });
            const pkgList = await getPackageList(SESSION_INFO.appProvinceShort, SESSION_INFO.firm.id.toString(), SESSION_INFO.sid);
            setPackages(pkgList);
            setIsPackagesDirty(false);
        } catch (e) {
            console.error("Error deactivePackages: ", e);
            setIsPackagesDirty(true);
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <>
            {isLoading && <Loading showMsg={true} extraMsg="Loading Packages" inline={true} />}
            
            {showPkgEditor && (
                <PackagesEditor
                    setShow={setShowPkgEditor}
                    packages={packages}
                    setPackages={setPackages}
                    savePackages={savePackages}
                    pkgEditorAction={pkgEditorAction}
                    pkgSelect={pkgSelect}
                    setPkgSelect={setPkgSelect}
                    setIsPackagesDirty={setIsPackagesDirty}
                />
            )}

            {showDeleteAlert && (
                <Modal
                    show={showDeleteAlert}
                    onHide={() => {setPkgSelect(null);setShowDeleteAlert(false);}}
                    backdrop="static"
                    keyboard={false}
                    centered
                >
                    <Modal.Header closeButton>
                        <Modal.Title>Warning</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Alert variant="danger"><p>This will delete your package <b>permanently</b> and <b>CANNOT BE UNDONE</b>.</p></Alert>
                        <p>Are you sure you want to delete this package <b>{pkgSelect.name}</b>?</p>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => {setPkgSelect(null);setShowDeleteAlert(false);}}>Cancel</Button>
                        <Button variant="primary" onClick={() => {handlePkgDelete(pkgSelect);setPkgSelect(null);setShowDeleteAlert(false);}}>Delete</Button>
                    </Modal.Footer>
                </Modal>
            )}

            {showDeactivateAlert && (
                <Modal
                    show={showDeactivateAlert}
                    onHide={() => {setShowDeactivateAlert(false);}}
                    backdrop="static"
                    keyboard={false}
                    centered
                >
                    <Modal.Header closeButton>
                        <Modal.Title>Warning</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Alert variant="danger">
                            <p>This will reset your Package list to our default, and remove all changes and new packages you've made. This <b>CANNOT BE UNDONE</b>.</p>
                            <p>Are you sure you want to reset the package list?</p>
                        </Alert>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => {setShowDeactivateAlert(false);}}>Cancel</Button>
                        <Button variant="primary" onClick={() => {
                            deactivePackages().then(() => {
                                setShowDeactivateAlert(false);
                                dispatch(setShowPackageSaveWarning(true));
                            });
                        }}>Reset</Button>
                    </Modal.Footer>
                </Modal>
            )}

            <Card className="card-list card-search" style={{height:'100%'}}>
                <Card.Header>
                    <h2>Packages</h2>
                    <InputGroup className="search-bar">
                        <InputGroup.Text>
                            <i style={{cursor: "default"}} className="fa-duotone fa-magnifying-glass fa-indicator"/>
                        </InputGroup.Text>
                        <Form.Control
                            type="text" 
                            placeholder="Type to search for a package or document..." 
                            onChange={(e) => setDebouncedSearchPkgText(e.target.value)} 
                            value={debouncedSearchPkgText}
                        />

                        <div className="buttons-container">
                            {debouncedSearchPkgText > '' && (
                                <OverlayTrigger key="clearSearchButton" placement="bottom" delay={300} overlay={<Tooltip>Clear search</Tooltip>}>
                                    <Button variant="link" id="clearSearchBtn" onClick={() => {
                                        if (debouncedSearchPkgText !== '')
                                            setDebouncedSearchPkgText('');
                                    }}>
                                        <i id="clearSearchBtn2" className="fa-light fa-xmark"/>
                                    </Button>
                                </OverlayTrigger>
                            )}

                            {(filtersSelect.filter((tag) => tag).length > 0) && (
                                <>
                                    {/* only show root tag filters that selected */}
                                    {filtersSelect.filter((tag) => (tag && (tag.parentNames? !tag.parentNames.some((prtNm) => (filtersSelect.some((f) => f.name === prtNm))) : true))).map((tag) => (
                                        <OverlayTrigger key={`${tag.name}`} placement="bottom" delay={300} overlay={<Tooltip >Remove tag</Tooltip>}>
                                            <Badge className="tag removable" pill bg="info" key={tag.name} onClick={() => handleFilterClick(tag)}>{tag.name}</Badge>
                                        </OverlayTrigger>
                                    ))}
                                </>
                            )}

                            <OverlayTrigger 
                                key="openFilterPopoverButton" 
                                trigger="click"
                                placement="bottom"
                                overlay={filterPopover}
                                rootClose
                            >
                                <Button variant="link"><i className="fa-regular fa-bars-filter"/>Filter</Button>
                            </OverlayTrigger>
                        </div>
                    </InputGroup>

                    <OverlayTrigger key="addNewDocButton" placement="bottom" delay={300} overlay={<Tooltip >Add new package</Tooltip>}>
                        <Button variant="primary" onClick={() => handlePkgEditor("CreateNew")}><i className="fa-light fa-plus"/>Package</Button>
                    </OverlayTrigger>

                    <Dropdown>
                        <Dropdown.Toggle variant="link">
                            <i className="fa-solid fa-ellipsis-v"/>
                        </Dropdown.Toggle>

                        <Dropdown.Menu>
                            <Dropdown.Item onClick={() => setShowDeactivateAlert(true)}><i className="fa-regular fa-trash"/>&nbsp;Reset Packages</Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </Card.Header>

                <div className="alert-area">
                    <Alert
                        show={showPackageSaveWarning}
                        variant="warning"
                        onClose={() => dispatch(setShowPackageSaveWarning(false))}
                        dismissible
                    >
                        Please refresh your browser to see these changes reflected in your file.
                    </Alert>
                </div>

                <Card.Body>
                    <Table hover>
                        <thead>
                            <tr>
                                <th style={{width: '55%'}}>Package</th>
                                <th style={{width: '80px'}}>File Type</th>
                                <th style={{width: '80px'}}>Created</th>
                                <th>Tags</th>
                                <th></th>
                            </tr>
                        </thead>

                        <tbody>
                            {pkgsFiltered.map((pkgs, pkgsIdx) => (
                                pkgs.children ? pkgs.children.map((pkg, pkgIdx) => (
                                    <React.Fragment key={`pkgs-${pkgsIdx}-${pkgIdx}`}>
                                        {/* List Packages */}
                                        <tr>
                                            <OverlayTrigger key={`pkg-${pkg.name}`} placement="bottom" delay={300} overlay={<Tooltip>Show documents</Tooltip>}>
                                                <td className="nowrap" onClick={() => handlePkgClick(pkg)}>
                                                    <i className={pkgsExpand.some((p) => p.name === pkg.name && hasSomeTags([p, pkg], psm)) && pkg.doc_list ? "fa-regular fa-folder-open" : "fa-solid fa-folder"} style={{marginRight: '5px'}}></i>
                                                    {pkg.name}
                                                </td>
                                            </OverlayTrigger>
                                            <td>
                                                <div className="badge-file-container">
                                                    <Badge bg={pkgs.tags.includes(P) ? "info" : "secondary"} text="dark">P</Badge>
                                                    <Badge bg={pkgs.tags.includes(S) ? "info" : "secondary"} text="dark">S</Badge>
                                                    <Badge bg={pkgs.tags.includes(M) ? "info" : "secondary"} text="dark">M</Badge>
                                                </div>
                                            </td>
                                            <td></td>
                                            <td></td>
                                            <td align="right">
                                                <OverlayTrigger placement="bottom" delay={300} overlay={<Tooltip>Edit Package</Tooltip>}>
                                                    <i onClick={() => handlePkgEditor("AddDoc", pkg)} className="fa-regular fa-pen hover-option"/>
                                                </OverlayTrigger>
                                                { pkgIdx > 0 && searchPkgText === '' ? (
                                                    <OverlayTrigger placement="bottom" delay={300} overlay={<Tooltip>Move Package Up</Tooltip>}>
                                                        <i onClick={() => handlePkgMove(pkg, pkgIdx, "up")} className="fa-regular fa-arrow-up hover-option"/>
                                                    </OverlayTrigger>
                                                ) : (
                                                    <i className="fa-regular fa-arrow-up hover-option" style={{opacity: 0.25}}/>
                                                )}
                                                {pkgIdx < pkgs.children.length - 1 && searchPkgText === '' ? (
                                                    <OverlayTrigger placement="bottom" delay={300} overlay={<Tooltip>Move Package Down</Tooltip>}>
                                                        <i onClick={() => handlePkgMove(pkg, pkgIdx, "down")} className="fa-regular fa-arrow-down hover-option"/>
                                                    </OverlayTrigger>
                                                ) : (
                                                    <i className="fa-regular fa-arrow-down hover-option" style={{opacity: 0.25}}/>
                                                )}
                                                <OverlayTrigger placement="bottom" delay={300} overlay={<Tooltip>Rename Package</Tooltip>}>
                                                    <i onClick={() => handlePkgEditor("Rename", pkg)} className="fa-regular fa-input-text hover-option"/>
                                                </OverlayTrigger>
                                                <Dropdown>
                                                    <Dropdown.Toggle as="a" style={{ marginLeft: '10px' }}>
                                                        <i className="fa-solid fa-ellipsis-v"/>
                                                    </Dropdown.Toggle>

                                                    <Dropdown.Menu>
                                                        <Dropdown.Item onClick={() => handlePkgCopy(pkg)}><i className="fa-regular fa-copy"/>&nbsp;Copy this Package</Dropdown.Item>
                                                        <Dropdown.Item onClick={() => {
                                                            setPkgSelect(pkg);
                                                            setShowDeleteAlert(true);
                                                        }}><i className="fa-regular fa-trash"/>&nbsp;Delete this Package</Dropdown.Item>
                                                        <Dropdown.Item onClick={() => handlePkgCondtionSwitch(pkg)}><i className="fa-regular fa-toggle-off"/>&nbsp;Show/Hide Doc Conditions</Dropdown.Item>
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            </td>
                                        </tr>

                                        {pkgsExpand.some((p) => p.name === pkg.name && p.tags?.some((tag) => psm.includes(tag) && pkg.tags.includes(tag))) && pkg.doc_list ? pkg.doc_list.map((doc, docIdx) => (
                                            <React.Fragment key={`doc-${doc.doc_id}-${docIdx}`}>
                                                {/* List Documents */}
                                                <tr>
                                                    <OverlayTrigger placement="bottom" delay={300} overlay={!doc.is_uneditable? <Tooltip>Edit Document</Tooltip> : <></>}>
                                                        <td className="nowrap" onClick={() => {
                                                            if (doc.is_uneditable) {
                                                                alert("This document is not editable.");
                                                            } else {
                                                                navDocEditor(doc, SESSION_INFO, nav);
                                                            }
                                                        }}>
                                                            {doc.template_type_id === 10 ? (
                                                                <i className="fa-solid fa-file-pdf status-pdf" style={{marginRight: '5px', marginLeft: '20px'}}></i>
                                                            ) : doc.tags?.includes(CompoundDocument) ? (
                                                                <i className="fa-solid fa-memo status-compound" style={{marginRight: '5px', marginLeft: '20px'}}></i>
                                                            ) : (
                                                                <i className="fa-solid fa-file-word status-word" style={{marginRight: '5px', marginLeft: '20px'}}></i>
                                                            )}
                                                            {doc.name}
                                                        </td>
                                                    </OverlayTrigger>

                                                    <td></td>
                                                    
                                                    <td>
                                                        {doc.tags.includes(User) ? (
                                                            <Badge bg="success" text="dark" pill>User</Badge>
                                                        ) : doc.tags.includes(Custom) ? (
                                                            <Badge bg="custom" text="dark" pill>Custom</Badge>
                                                        ) : (
                                                            <Badge bg="system" text="dark" pill>System</Badge>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {doc.tags.filter((tag) => (![...psm, ...creators].includes(tag) && (tag.children? !tag.children.some((chd) => (doc.tags.includes(chd))) : true))).map((tag, index) => (
                                                            <Badge key={`tag-${doc.doc_id}-${tag.name}-${index}`} bg="info" text="dark" pill>{tag.name}</Badge>
                                                        ))}
                                                    </td>
                                                    <td align="right">
                                                        {!doc.is_uneditable && (
                                                            <OverlayTrigger placement="bottom" delay={300} overlay={<Tooltip>Edit Document</Tooltip>}>
                                                                <i onClick={() => navDocEditor(doc, SESSION_INFO, nav)} className="fa-regular fa-pen hover-option"></i>
                                                            </OverlayTrigger>
                                                        )}
                                                        {docIdx > 0? (
                                                            <OverlayTrigger placement="bottom" delay={300} overlay={<Tooltip>Move Document Up</Tooltip>}>
                                                                <i onClick={() => handleDocMove(doc, docIdx, pkg, "up")} className="fa-regular fa-arrow-up hover-option"/>
                                                            </OverlayTrigger>
                                                        ) : (
                                                            <i className="fa-regular fa-arrow-up hover-option" style={{opacity: 0.25}}/>
                                                        )}    
                                                        {docIdx < pkg.doc_list.length - 1? (
                                                            <OverlayTrigger placement="bottom" delay={300} overlay={<Tooltip>Move Document Down</Tooltip>}>
                                                                <i onClick={() => handleDocMove(doc, docIdx, pkg, "down")} className="fa-regular fa-arrow-down hover-option"/>
                                                            </OverlayTrigger>
                                                        ) : (
                                                            <i className="fa-regular fa-arrow-down hover-option" style={{opacity: 0.25}}/>
                                                        )}
                                                        <OverlayTrigger placement="bottom" delay={300} overlay={<Tooltip>Set Document Condition</Tooltip>}>
                                                            <i onClick={() => handleDocSetCondition(doc, docIdx, pkg)} className="fa-regular fa-gear hover-option"/>
                                                        </OverlayTrigger>
                                                        <Dropdown>
                                                            <Dropdown.Toggle as="a" style={{ marginLeft: '10px' }}>
                                                                <i className="fa-solid fa-ellipsis-v"/>
                                                            </Dropdown.Toggle>

                                                            <Dropdown.Menu>
                                                                <Dropdown.Item onClick={() => handleDocDelete(doc, docIdx, pkg)}><i className="fa-regular fa-trash"/>&nbsp;Remove Document from Package</Dropdown.Item>
                                                                <Dropdown.Item onClick={() => handleDocPageNumberReset(docIdx, pkg)}><i className="fa-regular fa-toggle-off"/>&nbsp;Switch Page Number Reset</Dropdown.Item>
                                                            </Dropdown.Menu>
                                                        </Dropdown>
                                                    </td>
                                                </tr>

                                                {/* List package conditions */}
                                                {pkgsShowCondition.some((p) => (p.name === pkg.name)) && (
                                                    <tr>
                                                        <td colSpan={9}>
                                                            <div style={{marginLeft:'40px'}}>
                                                                <b>Condition: </b>
                                                                {doc.pkg_condition ? (
                                                                    <span dangerouslySetInnerHTML={{__html: doc.pkg_condition}}/>
                                                                ) : (
                                                                    <span>None</span>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        )) : null}
                                    </React.Fragment>
                                )) : null
                            ))}

                            {packages.length > 0 && (
                                <tr>
                                    <td colSpan={5}>
                                        <Alert variant="primary">{pkgsFiltered?.flatMap((pkgs) => pkgs.children).length === 0 ? "- No Search Result found -" : "- End of Results -"}</Alert>
                                    </td>
                                </tr>
                            )}

                            {packages.length === 0 && (
                                <tr>
                                    <td colSpan={5}>
                                        <Alert variant="primary">{"- Click \"+ Package\" to create new package -"}</Alert>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>
        </>
    )
}

export default Packages;